<script setup lang="ts">
import { PolicyEnforcement, ScreeningAction, ScreeningType } from '@factoryfixinc/ats-interfaces';
import { computed, ref } from 'vue';

import CopilotActivationService from '@/core/jobs/copilot-activation/copilot-activation.service';

import FormSection from './FormSection.vue';
import CommunicationPreferencesSection from '@/components/Jobs/CopilotActivation/ProjectCopilotConfiguration/CommunicationPreferencesSection.vue';
import ConfirmScreeningPreemption from './ConfirmScreeningPreemption.vue';
import ProjectService from '@/core/shared/project/project.service';
import InterviewSchedulingSettings from './InterviewSchedulingSettings.vue';
import RedirectToCareersPageOption from './ScreeningOptions/RedirectToCareersPageOption.vue';
import StickToSetQuestionsOption from './ScreeningOptions/StickToSetQuestionsOption.vue';
import InterviewAndDigDeeperOption from './ScreeningOptions/InterviewAndDigDeeperOption.vue';
import SkipAutomatedScreeningOption from './ScreeningOptions/SkipAutomatedScreeningOption.vue';
import type { ScreeningTypeWithSkip } from '@/core/jobs/copilot-activation/types/local-screener-question.type';

const copilotActivationService = new CopilotActivationService();
const projectService = new ProjectService();
const openScreeningWarningDialog = ref(false);
const hasShownScreeningTypeDialog = ref(false);
const pendingScreeningSelection = ref<{
  action: ScreeningAction;
  screeningType?: ScreeningTypeWithSkip;
} | null>(null);

const props = defineProps<{
  isDisabled: boolean;
  isContinueLogicEnabled: boolean;
  candidateAiDisclosurePolicy?: PolicyEnforcement;
  interviewSchedulingPolicy?: PolicyEnforcement;
  staticScreeningPolicy?: PolicyEnforcement;
  dynamicScreeningPolicy?: PolicyEnforcement;
  hasUrlScreeningEnabled?: boolean;
}>();

const selectedAction = computed(
  () => copilotActivationService.screeningAction ?? ScreeningAction.SCREEN,
);
const screeningType = computed(() => copilotActivationService.screeningType ?? null);
const showPassiveCandidateCallout = computed(() => copilotActivationService.activeOutreachEnabled);

const isDynamicScreeningLocked = computed(
  () => props.dynamicScreeningPolicy === PolicyEnforcement.FORBIDDEN,
);

const isStaticScreeningLocked = computed(
  () => props.staticScreeningPolicy === PolicyEnforcement.FORBIDDEN,
);

const isSkipScreeningLocked = computed(
  () =>
    props.dynamicScreeningPolicy === PolicyEnforcement.REQUIRED ||
    props.staticScreeningPolicy === PolicyEnforcement.REQUIRED,
);

const confirmActionLabel = computed(() =>
  pendingScreeningSelection.value?.action === ScreeningAction.URL
    ? 'Switch to careers page'
    : 'Confirm change',
);

const hasOngoingScreening = computed(() => {
  // Consider DYNAMIC or STATIC screening as ongoing
  return (
    selectedAction.value === ScreeningAction.SCREEN &&
    (screeningType.value === ScreeningType.DYNAMIC || screeningType.value === ScreeningType.STATIC)
  );
});

function isSameSelection(action: ScreeningAction, newScreeningType?: ScreeningTypeWithSkip) {
  if (selectedAction.value !== action) {
    return false;
  }

  if (action !== ScreeningAction.SCREEN) {
    return true;
  }

  return screeningType.value === (newScreeningType ?? null);
}

function shouldConfirmSelection(action: ScreeningAction, newScreeningType?: ScreeningTypeWithSkip) {
  if (hasShownScreeningTypeDialog.value) {
    return false;
  }

  if (!projectService.currentProject?.copilot) {
    return false;
  }

  if (!hasOngoingScreening.value) {
    return false;
  }

  return (
    action !== selectedAction.value ||
    (action === ScreeningAction.SCREEN && (newScreeningType ?? null) !== screeningType.value)
  );
}

function selectNewScreeningAction(action: ScreeningAction, screeningType?: ScreeningTypeWithSkip) {
  if (props.isDisabled || isSameSelection(action, screeningType)) {
    return;
  }

  if (shouldConfirmSelection(action, screeningType)) {
    pendingScreeningSelection.value = { action, screeningType };
    openScreeningWarningDialog.value = true;
    hasShownScreeningTypeDialog.value = true;
    return;
  }

  applyScreeningSelection(action, screeningType);
}

function applyScreeningSelection(action: ScreeningAction, screeningType?: ScreeningTypeWithSkip) {
  copilotActivationService.screeningAction = action;
  copilotActivationService.screeningType =
    action === ScreeningAction.SCREEN ? screeningType ?? null : null;
  openScreeningWarningDialog.value = false;
  pendingScreeningSelection.value = null;
}

function savePendingScreeningAction() {
  if (pendingScreeningSelection.value) {
    applyScreeningSelection(
      pendingScreeningSelection.value.action,
      pendingScreeningSelection.value.screeningType,
    );
    return;
  }
  openScreeningWarningDialog.value = false;
}

function cancelPendingScreeningAction() {
  pendingScreeningSelection.value = null;
  openScreeningWarningDialog.value = false;
  hasShownScreeningTypeDialog.value = false;
}
</script>

<template>
  <FormSection
    id="screening-style"
    title="Set your AI recruiter's interview style"
    subtitle="Decide how your AI recruiter should evaluate and interview candidates"
    class="border-b-0"
    :add-divider="false"
    :is-continue-logic-enabled="isContinueLogicEnabled"
  >
    <InterviewAndDigDeeperOption
      v-model:questions="
        copilotActivationService.projectCopilotConfigurationV1DynamicScreeningQuestions
      "
      :is-selected="
        selectedAction === ScreeningAction.SCREEN && screeningType === ScreeningType.DYNAMIC
      "
      :is-disabled="isDisabled"
      :is-policy-locked="isDynamicScreeningLocked"
      @click="selectNewScreeningAction(ScreeningAction.SCREEN, ScreeningType.DYNAMIC)"
    />

    <StickToSetQuestionsOption
      :is-selected="
        selectedAction === ScreeningAction.SCREEN && screeningType === ScreeningType.STATIC
      "
      :is-disabled="isDisabled"
      :is-policy-locked="isStaticScreeningLocked"
      :show-passive-candidate-callout="showPassiveCandidateCallout"
      class="mt-3"
      @click="selectNewScreeningAction(ScreeningAction.SCREEN, ScreeningType.STATIC)"
    />

    <RedirectToCareersPageOption
      v-if="hasUrlScreeningEnabled"
      v-model:employer-career-site-url="copilotActivationService.employerCareerSiteUrl"
      :is-selected="selectedAction === ScreeningAction.URL"
      :is-disabled="isDisabled"
      :is-policy-locked="isSkipScreeningLocked"
      class="mt-3"
      @click="selectNewScreeningAction(ScreeningAction.URL)"
    />

    <SkipAutomatedScreeningOption
      :is-selected="selectedAction === ScreeningAction.SCREEN && screeningType === 'SKIP'"
      :is-disabled="isDisabled"
      :is-policy-locked="isSkipScreeningLocked"
      class="mt-3"
      @click="selectNewScreeningAction(ScreeningAction.SCREEN, 'SKIP')"
    />

    <ConfirmScreeningPreemption
      @save="savePendingScreeningAction"
      @cancel="cancelPendingScreeningAction"
      :is-open="openScreeningWarningDialog"
      :is-loading="false"
      v-if="openScreeningWarningDialog"
      :confirm-action="confirmActionLabel"
    />

    <InterviewSchedulingSettings
      class="mt-10"
      :interview-scheduling-policy="interviewSchedulingPolicy"
      v-show="copilotActivationService.screeningAction !== ScreeningAction.URL"
    />

    <CommunicationPreferencesSection
      :candidate-ai-disclosure-policy="candidateAiDisclosurePolicy"
      class="mt-10"
    />
  </FormSection>
</template>
